import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { Loader2, Users, Save, Send, ArrowLeft, AlertTriangle, Eye } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { LEAD_STAGES } from "../../constants/leadStages";
import ToastMsg from "../../components/common/ToastMsg";
import EmailBodyEditor from "../../components/email/EmailBodyEditor";
import EmailPreviewModal from "../../components/email/EmailPreviewModal";
import {
    EMAIL_CAMPAIGNS_URL, EMAIL_CAMPAIGN_AUDIENCE_URL, EMAIL_CAMPAIGN_START_URL,
    EMAIL_ACCOUNTS_URL, EMAIL_TEMPLATES_URL, EMAIL_SENDING_POOL_URL,
} from "../../constants/api";

// Mirrors platform_core.audience.AUDIENCE_TARGETS.
const TARGETS = [
    { value: 'crm',              label: 'CRM leads',        hint: 'Emails the primary contact on each customer' },
    { value: 'engage_enquiries', label: 'Patient enquiries', hint: 'Engage tenants only' },
    { value: 'engage_patients',  label: 'Patients',          hint: 'Only those who opted into email' },
];


const Field = ({ label, hint, children }) => (
    <div className="space-y-1.5">
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{label}</label>
        {children}
        {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
    </div>
);

const selectCls =
    "w-full h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 " +
    "px-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500";

export default function EmailCampaignComposer() {
    const navigate = useNavigate();
    const { id } = useParams();
    const editing = Boolean(id);
    const { fetch, create, update } = useApiMethods();

    const [accounts,  setAccounts]  = useState([]);
    const [pool,      setPool]      = useState(null);
    const [templates, setTemplates] = useState([]);
    const [saving,    setSaving]    = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    const [form, setForm] = useState({
        name: "",
        account_id: "",
        template_id: "",
        subject: "",
        body_html: "",
        schedule_at: "",
        throttle_per_minute: 60,
    });
    const [audience, setAudience] = useState({ target: "crm" });

    // Live audience preview — without it the composer is a blind send: a filter
    // matching nobody looks identical to one matching the whole database.
    const [preview, setPreview] = useState({ count: null, sample: [], loading: false });
    const previewSeq = useRef(0);

    const set   = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setAud = (k, v) => setAudience(a => ({ ...a, [k]: v || undefined }));

    useEffect(() => {
        // Default is the pool, so no account is preselected — a single mailbox
        // cannot carry a large audience on its own.
        fetch(EMAIL_ACCOUNTS_URL).then(r => {
            if (r?.status === 'success') setAccounts(r.data || []);
        }).catch(() => {});
        fetch(EMAIL_TEMPLATES_URL).then(r => {
            if (r?.status === 'success') setTemplates(r.data || []);
        }).catch(() => {});
        fetch(EMAIL_SENDING_POOL_URL).then(r => {
            if (r?.status === 'success') setPool(r.data);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!editing) return;
        fetch(`${EMAIL_CAMPAIGNS_URL}?id=${id}`).then(r => {
            if (r?.status !== 'success') return;
            const c = r.data;
            setForm({
                name: c.name || "",
                account_id: c.account_id ? String(c.account_id) : "",
                template_id: c.template_id ? String(c.template_id) : "",
                subject: c.subject || "",
                body_html: c.body_html || "",
                schedule_at: c.schedule_at ? String(c.schedule_at).slice(0, 16) : "",
                throttle_per_minute: c.throttle_per_minute || 60,
            });
            setAudience(c.audience_filter || { target: 'crm' });
        }).catch(() => {});
    }, [id, editing]); // eslint-disable-line react-hooks/exhaustive-deps

    const runPreview = useCallback(async (aud) => {
        const seq = ++previewSeq.current;
        setPreview(p => ({ ...p, loading: true }));
        try {
            const res = await create(EMAIL_CAMPAIGN_AUDIENCE_URL, { audience_filter: aud });
            // Ignore a slow response that a newer edit has already superseded.
            if (seq !== previewSeq.current) return;
            if (res?.status === 'success') {
                setPreview({ count: res.data.count, sample: res.data.sample || [], loading: false });
            } else {
                setPreview({ count: null, sample: [], loading: false });
            }
        } catch {
            if (seq === previewSeq.current) setPreview({ count: null, sample: [], loading: false });
        }
    }, [create]);

    useEffect(() => {
        const t = setTimeout(() => runPreview(audience), 400);
        return () => clearTimeout(t);
    }, [audience]); // eslint-disable-line react-hooks/exhaustive-deps

    // How long this send will actually take. A 2,479-person audience against a
    // 1,200/day pool is a two-day campaign, and saying so up front beats the
    // operator discovering it when the run pauses overnight.
    const capacity = form.account_id
        ? (accounts.find(a => String(a.id) === String(form.account_id))?.daily_send_limit || 0)
        : (pool?.daily_capacity || 0);
    const days = capacity > 0 && preview.count ? Math.ceil(preview.count / capacity) : 0;
    const poolHint = !pool
        ? undefined
        : pool.account_count === 0
            ? "No mailbox is enabled for sending — set one up under Email → Sending Pool."
            : `${pool.account_count} mailbox${pool.account_count === 1 ? '' : 'es'}, `
              + `${pool.daily_capacity.toLocaleString()}/day, `
              + `${pool.remaining_today.toLocaleString()} left today.`;

    const chosenTemplate = templates.find(t => String(t.id) === String(form.template_id));
    const effectiveSubject = form.subject || chosenTemplate?.subject || "";
    const hasBody = Boolean(form.body_html.trim() || chosenTemplate);

    const save = async ({ startNow = false } = {}) => {
        if (!form.name.trim()) return ToastMsg("error", "Give the campaign a name");
        if (!hasBody)          return ToastMsg("error", "Pick a template or write the email body");
        // An empty account means the pool, which is the default — but the pool
        // has to actually contain something.
        if (!form.account_id && pool && pool.account_count === 0) {
            return ToastMsg("error", "No mailbox is enabled for sending. Set one up under Email → Sending Pool.");
        }

        setSaving(true);
        const payload = {
            ...form,
            account_id: form.account_id ? Number(form.account_id) : null,
            template_id: form.template_id ? Number(form.template_id) : null,
            throttle_per_minute: Number(form.throttle_per_minute) || 60,
            schedule_at: form.schedule_at || null,
            audience_filter: audience,
        };
        try {
            const res = editing
                ? await update(EMAIL_CAMPAIGNS_URL, { id: Number(id), ...payload })
                : await create(EMAIL_CAMPAIGNS_URL, payload);

            if (res?.status !== 'success') {
                ToastMsg("error", res?.message || "Could not save campaign");
                setSaving(false);
                return;
            }
            const campaignId = editing ? Number(id) : res.data.id;

            if (startNow) {
                const s = await create(EMAIL_CAMPAIGN_START_URL(campaignId), {});
                if (s?.status === 'success') ToastMsg("success", "Campaign started");
                else ToastMsg("error", s?.message || "Saved, but could not start");
            } else {
                ToastMsg("success", editing ? "Campaign updated" : "Campaign saved");
            }
            navigate('/email/campaigns');
        } catch {
            ToastMsg("error", "Could not save campaign");
        }
        setSaving(false);
    };

    const target = audience.target || 'crm';

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/email/campaigns')}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                {editing ? 'Edit Campaign' : 'New Email Campaign'}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                Tracking pixel and link rewriting are added automatically
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* ── content ── */}
                    <div className="lg:col-span-2 space-y-4">
                        <Field label="Campaign name">
                            <Input value={form.name} onChange={e => set('name', e.target.value)}
                                   placeholder="August re-engagement" />
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field
                                label="Send from"
                                hint={form.account_id
                                    ? "Pinned to one mailbox — capped by that mailbox's daily limit."
                                    : poolHint}>
                                <select className={selectCls} value={form.account_id}
                                        onChange={e => set('account_id', e.target.value)}>
                                    <option value="">
                                        Sending pool{pool ? ` — ${pool.account_count} mailbox${pool.account_count === 1 ? '' : 'es'}` : ''}
                                    </option>
                                    {accounts.filter(a => a.can_send_campaigns).map(a => (
                                        <option key={a.id} value={a.id}>{a.email_address}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Template" hint="Optional — or write the copy below">
                                <select className={selectCls} value={form.template_id}
                                        onChange={e => set('template_id', e.target.value)}>
                                    <option value="">No template</option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <Field label="Subject"
                               hint={chosenTemplate && !form.subject
                                   ? `Using the template subject: "${chosenTemplate.subject}"`
                                   : "Supports {{name}} and {{email}}"}>
                            <Input value={form.subject} onChange={e => set('subject', e.target.value)}
                                   placeholder={chosenTemplate?.subject || "Hello {{name}}"} />
                        </Field>

                        <Field label="Body (HTML)"
                               hint={chosenTemplate
                                   ? "Leave empty to use the template body. Anything here overrides it."
                                   : "Supports {{name}} and {{email}}"}>
                            <EmailBodyEditor
                                value={form.body_html}
                                onChange={v => set('body_html', v)}
                                height={260}
                                placeholder="Hi {{name}}, …"
                            />
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Schedule for" hint="Leave empty to save as a draft">
                                <Input type="datetime-local" value={form.schedule_at}
                                       onChange={e => set('schedule_at', e.target.value)} />
                            </Field>
                            <Field label="Throttle (emails per minute)"
                                   hint="Gmail and Outlook rate-limit hard; 60 is a safe default">
                                <Input type="number" min={1} max={600} value={form.throttle_per_minute}
                                       onChange={e => set('throttle_per_minute', e.target.value)} />
                            </Field>
                        </div>
                    </div>

                    {/* ── audience ── */}
                    <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                            <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-400" />
                                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Audience</h3>
                            </div>

                            <Field label="Send to">
                                <select className={selectCls} value={target}
                                        onChange={e => setAudience({ target: e.target.value })}>
                                    {TARGETS.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <p className="text-[11px] text-gray-400 -mt-2">
                                {TARGETS.find(t => t.value === target)?.hint}
                            </p>

                            {target === 'crm' && (
                                <>
                                    <Field label="Lead stage">
                                        <select className={selectCls} value={audience.lead_stage || ''}
                                                onChange={e => setAud('lead_stage', e.target.value)}>
                                            <option value="">Any stage</option>
                                            {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="City">
                                        <Input value={audience.city || ''} placeholder="Any city"
                                               onChange={e => setAud('city', e.target.value)} />
                                    </Field>
                                </>
                            )}

                            {target === 'engage_patients' && (
                                <>
                                    <Field label="City">
                                        <Input value={audience.city || ''} placeholder="Any city"
                                               onChange={e => setAud('city', e.target.value)} />
                                    </Field>
                                    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                                        <input type="checkbox" checked={Boolean(audience.is_vip)}
                                               onChange={e => setAud('is_vip', e.target.checked)} />
                                        VIP patients only
                                    </label>
                                    <p className="flex gap-1.5 text-[11px] text-amber-600 dark:text-amber-500">
                                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                                        Only patients who consented to marketing <em>and</em> chose email are
                                        included. That preference defaults to off, so reach starts small.
                                    </p>
                                </>
                            )}

                            {target === 'engage_enquiries' && (
                                <Field label="Status">
                                    <Input value={audience.status || ''} placeholder="Any status"
                                           onChange={e => setAud('status', e.target.value)} />
                                </Field>
                            )}

                            {/* preview */}
                            <div className="rounded-md bg-gray-50 dark:bg-gray-800/60 p-3">
                                {preview.loading ? (
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Counting…
                                    </div>
                                ) : preview.count === null ? (
                                    <p className="text-xs text-gray-400">Audience unavailable</p>
                                ) : (
                                    <>
                                        <p className={cn(
                                            "text-sm font-semibold",
                                            preview.count === 0 ? "text-amber-600 dark:text-amber-500"
                                                                : "text-gray-800 dark:text-gray-100"
                                        )}>
                                            {preview.count.toLocaleString()} recipient{preview.count === 1 ? '' : 's'}
                                        </p>
                                        {preview.count === 0 && (
                                            <p className="text-[11px] text-amber-600 dark:text-amber-500 mt-0.5">
                                                Nobody matches — check the filters, or whether these records have
                                                email addresses.
                                            </p>
                                        )}
                                        {days > 1 && (
                                            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-500">
                                                Exceeds one day of sending capacity ({capacity.toLocaleString()}/day) —
                                                this will run over about {days} days, pausing overnight.
                                            </p>
                                        )}
                                        {preview.sample.length > 0 && (
                                            <ul className="mt-2 space-y-0.5">
                                                {preview.sample.slice(0, 5).map((s, i) => (
                                                    <li key={i} className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                                                        {s.name || '—'} · {s.email}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Button onClick={() => save()} disabled={saving}
                                    className="flex items-center justify-center gap-2 text-sm">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                {form.schedule_at ? 'Save & schedule' : 'Save draft'}
                            </Button>
                            <Button variant="outline" type="button"
                                    onClick={() => setPreviewOpen(true)}
                                    disabled={!hasBody}
                                    className="flex items-center justify-center gap-2 text-sm">
                                <Eye className="h-4 w-4" /> Preview
                            </Button>
                            <Button variant="outline" onClick={() => save({ startNow: true })}
                                    disabled={saving || !preview.count}
                                    className="flex items-center justify-center gap-2 text-sm">
                                <Send className="h-4 w-4" /> Save & send now
                            </Button>
                        </div>
                    </div>
                </div>
            </ContainerCard>

            <EmailPreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                body_html={form.body_html || chosenTemplate?.body_html || ''}
                subject={effectiveSubject}
            />
        </div>
    );
}
